import { MentionData, MENTION_TYPES } from "@adsk/acc-mention";
import { Company, Role, User } from "@adsk/alloy-provider";

type MentionAnalytics = {
  numberOfMentions: number;
  numOfUsers: number;
  numOfRoles: number;
  numOfCompanies: number;
  numOfAggregatedMentionees: number;
};

export const getCommentsMentionAnalytics = ({
  comment,
  users,
  rolesList,
  companiesList,
}: {
  comment: string;
  users: User[];
  rolesList: Role[];
  companiesList: Company[];
}): MentionAnalytics => {
  const mentionRegex = /@{(.*?)}/;

  const mentions: MentionData[] | undefined = comment
    .match(new RegExp(mentionRegex, "g"))
    // remove the @ at the start
    ?.map((mention: string) => mention.substring(1))
    .map((mentionJsonString: string) => {
      try {
        return JSON.parse(mentionJsonString);
      } catch {
        return null;
      }
    })
    .filter(mention => !!mention);

  if (!mentions || mentions.length === 0) {
    return {
      numberOfMentions: 0,
      numOfUsers: 0,
      numOfRoles: 0,
      numOfCompanies: 0,
      numOfAggregatedMentionees: 0,
    };
  }

  const initialMentioneesData = {
    numberOfMentions: 0,
    numOfUsers: 0,
    numOfRoles: 0,
    numOfCompanies: 0,
    aggregatedMentioneeIds: [] as string[],
  };
  const numOfMentioneesAggregator = mentions
    ? mentions.reduce((acc, mention) => {
        switch (mention.type) {
          case MENTION_TYPES.USER:
            return {
              ...acc,
              numOfUsers: acc.numOfUsers + 1,
              aggregatedMentioneeIds: [
                ...acc.aggregatedMentioneeIds,
                mention.id,
              ],
            };
          case MENTION_TYPES.ROLE:
            const role = rolesList.find(
              role => role.memberGroupId === mention.id
            );
            const allUsersInRole = role
              ? users.filter(user => user.roleIds.includes(role.id))
              : [];
            return {
              ...acc,
              numOfRoles: acc.numOfRoles + 1,
              aggregatedMentioneeIds: [
                ...acc.aggregatedMentioneeIds,
                ...allUsersInRole.map(user => user.autodeskId),
              ],
            };
          case MENTION_TYPES.COMPANY:
            const company = companiesList.find(
              company => company.memberGroupId === mention.id
            ) as (Company & { memberCount: number }) | undefined;
            const allUsersInCompany = company
              ? users.filter(user => user.companyId === company.id)
              : [];
            return {
              ...acc,
              numOfCompanies: acc.numOfCompanies + 1,
              aggregatedMentioneeIds: [
                ...acc.aggregatedMentioneeIds,
                ...allUsersInCompany.map(user => user.autodeskId),
              ],
            };
          default:
            return acc;
        }
      }, initialMentioneesData)
    : initialMentioneesData;

  return {
    numberOfMentions: mentions.length,
    numOfUsers: numOfMentioneesAggregator.numOfUsers,
    numOfRoles: numOfMentioneesAggregator.numOfRoles,
    numOfCompanies: numOfMentioneesAggregator.numOfCompanies,
    numOfAggregatedMentionees: new Set(
      numOfMentioneesAggregator.aggregatedMentioneeIds
    ).size,
  };
};
